import { memo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import './PanelCompositionTransition.css';

const HEIGHT_EPSILON_PX = 1;
const FALLBACK_TRANSITION_MS = 250;
const TRANSITION_SAFETY_MS = 80;

function readElementHeight(node) {
  if (!node) return 0;
  const rect = typeof node.getBoundingClientRect === 'function'
    ? node.getBoundingClientRect()
    : null;
  return Math.max(0, Math.round(rect?.height || node.offsetHeight || 0));
}

function normalizeObservedHeight(entry, fallbackNode) {
  const borderBox = Array.isArray(entry?.borderBoxSize)
    ? entry.borderBoxSize[0]
    : entry?.borderBoxSize;

  if (borderBox && Number.isFinite(borderBox.blockSize)) {
    return Math.max(0, Math.round(borderBox.blockSize));
  }

  if (Number.isFinite(entry?.contentRect?.height)) {
    return Math.max(0, Math.round(entry.contentRect.height));
  }

  return readElementHeight(fallbackNode);
}

function parseCssTime(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return 0;
  if (normalized.endsWith('ms')) return Number.parseFloat(normalized) || 0;
  if (normalized.endsWith('s')) return (Number.parseFloat(normalized) || 0) * 1000;
  return 0;
}

function getTransitionTotalMs(node) {
  if (!node || typeof window.getComputedStyle !== 'function') {
    return FALLBACK_TRANSITION_MS;
  }

  const style = window.getComputedStyle(node);
  const durations = String(style.transitionDuration || '').split(',').map(parseCssTime);
  const delays = String(style.transitionDelay || '').split(',').map(parseCssTime);
  const totals = durations.map((duration, index) => (
    duration + (delays[index % Math.max(1, delays.length)] || 0)
  ));
  const measuredDuration = Math.max(0, ...totals);
  return measuredDuration > 0 ? measuredDuration : FALLBACK_TRANSITION_MS;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Smooths height changes when a mode, tab, or segmented choice swaps panel composition.
 * Nested disclosures keep ownership of their own open/close motion; this component only
 * animates when transitionKey changes.
 *
 * It owns the END of its own flight -- `transitionend`, or a timer padded past the duration it
 * measures from the live computed style -- and reports it through the optional `onSettle(key)`.
 * Anything outside that must switch at that moment derives from this callback instead of
 * predicting the duration with a timer of its own.
 */
const PanelCompositionTransition = ({
  children,
  transitionKey,
  className = '',
  onContainerRef = null,
  heightOverride = null,
  onSettle = null,
}) => {
  const containerRef = useRef(null);

  /*
   * Hands the animating container out to a caller that needs to observe it. Additive and
   * optional: the container's height is what this component transitions, so anything
   * positioning itself against that moving edge has to watch this element rather than the
   * parent, which does not move.
   */
  const attachContainer = useCallback((node) => {
    containerRef.current = node;
    if (typeof onContainerRef === 'function') onContainerRef(node);
  }, [onContainerRef]);
  const contentRef = useRef(null);
  const previousKeyRef = useRef(transitionKey);
  const previousHeightRef = useRef(null);
  const targetHeightRef = useRef(null);
  const frameRef = useRef(null);
  const finishTimerRef = useRef(null);
  const resizingRef = useRef(false);

  /*
   * `onSettle` and `heightOverride` are read at REST TIME, not at render time, so they live in
   * refs rather than in `finishResize`'s dependency list. That list is load-bearing: the
   * `transitionend` effect below cleans up by calling `finishResize`, so a new identity would
   * tear down and re-arm mid-flight and abort the very animation it is timing. Updated in a
   * layout effect declared before the keyed effect, so a settle that happens in the same commit
   * still sees the current values.
   */
  const onSettleRef = useRef(onSettle);
  const heightOverrideRef = useRef(heightOverride);
  useLayoutEffect(() => {
    onSettleRef.current = onSettle;
    heightOverrideRef.current = heightOverride;
  });

  const clearScheduledMotion = useCallback(() => {
    if (frameRef.current != null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    if (finishTimerRef.current != null) {
      clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
  }, []);

  const finishResize = useCallback(() => {
    clearScheduledMotion();
    const container = containerRef.current;
    if (container) {
      container.classList.remove('panel-composition-transition--resizing');
      container.style.height = '';
    }
    resizingRef.current = false;
    targetHeightRef.current = null;
    /*
     * Announced BEFORE anything reads layout again, and in the same synchronous task as the
     * release above. A caller whose stylesheet has to switch at the end of the flight -- the
     * animation dock removes a `collapsed` attribute here -- would otherwise have a window in
     * which this component has let go of the height and CSS has not yet been told to hold it.
     * That window was ~117ms, and the panel visibly fell back to its collapsed height inside it.
     */
    onSettleRef.current?.(previousKeyRef.current);
    /*
     * Where the caller supplies the heights, its number is the memory. Measuring the content is
     * wrong for exactly those callers: they stretch it to fill this box, so the reading is the
     * box's own height, and after an expand that is still the COLLAPSED height -- which then
     * becomes the next flight's starting point and makes the following collapse a pop.
     */
    previousHeightRef.current = Number.isFinite(heightOverrideRef.current)
      ? Math.round(heightOverrideRef.current)
      : readElementHeight(contentRef.current);
  }, [clearScheduledMotion]);

  const scheduleFinish = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (finishTimerRef.current != null) clearTimeout(finishTimerRef.current);
    finishTimerRef.current = window.setTimeout(
      finishResize,
      getTransitionTotalMs(container) + TRANSITION_SAFETY_MS,
    );
  }, [finishResize]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleTransitionEnd = (event) => {
      if (
        resizingRef.current
        && event.target === container
        && event.propertyName === 'height'
      ) {
        finishResize();
      }
    };

    container.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      container.removeEventListener('transitionend', handleTransitionEnd);
      finishResize();
    };
  }, [finishResize]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver !== 'function') return undefined;

    const observer = new ResizeObserver((entries) => {
      const matchingEntry = entries.find((entry) => entry.target === content) || entries[0];
      if (Number.isFinite(heightOverride)) return;
      const nextHeight = normalizeObservedHeight(matchingEntry, content);
      previousHeightRef.current = nextHeight;

      if (!resizingRef.current) return;
      targetHeightRef.current = nextHeight;
      if (frameRef.current == null && containerRef.current) {
        containerRef.current.style.height = `${nextHeight}px`;
        scheduleFinish();
      }
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, [heightOverride, scheduleFinish]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    /*
     * A caller that already knows both heights supplies them. Measuring the content is only
     * correct while the content sizes itself; where a caller stretches it to fill this box, the
     * measurement returns this box's own height in both states, the two readings match, and the
     * epsilon branch below skips the animation every time.
     */
    const nextHeight = Number.isFinite(heightOverride)
      ? Math.round(heightOverride)
      : readElementHeight(content);
    if (previousHeightRef.current == null) {
      previousHeightRef.current = nextHeight;
      previousKeyRef.current = transitionKey;
      return;
    }

    const keyChanged = !Object.is(previousKeyRef.current, transitionKey);
    previousKeyRef.current = transitionKey;
    if (!keyChanged) return;

    const startHeight = resizingRef.current
      ? readElementHeight(container)
      : previousHeightRef.current;
    targetHeightRef.current = nextHeight;
    clearScheduledMotion();

    if (
      prefersReducedMotion()
      || Math.abs(startHeight - nextHeight) <= HEIGHT_EPSILON_PX
    ) {
      finishResize();
      previousHeightRef.current = nextHeight;
      return;
    }

    resizingRef.current = true;
    container.classList.add('panel-composition-transition--resizing');
    const previousInlineTransition = container.style.transition;
    container.style.transition = 'none';
    container.style.height = `${startHeight}px`;
    void container.offsetHeight;
    container.style.transition = previousInlineTransition;

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const activeContainer = containerRef.current;
      if (!activeContainer) return;
      activeContainer.style.height = `${targetHeightRef.current ?? nextHeight}px`;
      scheduleFinish();
    });
  }, [clearScheduledMotion, finishResize, heightOverride, scheduleFinish, transitionKey]);

  const classes = [
    'panel-composition-transition',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={attachContainer}
      className={classes}
      data-panel-composition-transition="true"
      data-transition-key={String(transitionKey ?? '')}
    >
      <div ref={contentRef} className="panel-composition-transition__content">
        {children}
      </div>
    </div>
  );
};

export default memo(PanelCompositionTransition);
